<template>
  <v-card
    flat
    width="354"
    class="my-gift-card primary-font"
    :key="item.barcode"
  >
    <transition name="flip">
      <div class="custom-img" ref="customImg" v-bind:key="flipped">
        <div class="flip-button" v-if="flipButtonAvailable">
          <v-btn
            depressed
            dark
            fab
            small
            @click="rotateImage()"
            color="var(--v-primary-base)"
          >
            <v-icon dark>fxt-rotate-left</v-icon>
          </v-btn>
        </div>
        <div v-if="flipped">
          <video class="video" controls v-if="item.messageUrlType == 'VIDEO'">
            <source :src="item.messageUrl" type="video/mp4" />
          </video>
          <v-img v-else :src="item.messageUrl"></v-img>
        </div>
        <div v-else>
          <v-img height="222" :src="item.imageUrl"> </v-img>
        </div>
      </div>
    </transition>
    <div class="custom-info">
      <div class="header">
        <div class="custom-title">{{ item.name }}: {{ giftCardValue }}</div>
        <div class="from"><span>Fra:</span> {{ item.giftCardFrom }}</div>
        <div class="chat" align="left" v-if="item.message">
          <div class="triangle"></div>
          <div class="content" v-html="formatText(item.message)"></div>
        </div>
      </div>
      <div class="holder">
        <div class="field" align="left">
          <span v-if="shouldDisplayExpirationDate"
            >Kortet utløper {{ formatDate(item.expirationDate) }}</span
          ><br />
          <span v-if="shouldDisplayUsageDescription">
            {{ item.usageDescription }}</span
          >
        </div>
      </div>
      <div class="custom-button">
        <v-btn
          class="fixit-btn"
          :class="[buttonDisabled ? 'disabled' : '']"
          depressed
          large
          rounded
          dark
          block
          color="var(--v-primary-base)"
          @click="showBarcode"
          >{{ giftCardButtonText }}</v-btn
        >
      </div>
    </div>
  </v-card>
</template>
<script>
import moment from "moment";
import GiftCardUsageArea from "./enums/GiftCardUsageAreaEnum.js";
import GiftCardStatus from "./enums/GiftCardStatusEnum.js";
export default {
  name: "MyGiftCard",
  data() {
    return {
      flipped: false,
    };
  },
  props: {
    /**
     * MyGiftCard item object
     * Properties
     *  barcode - required
     *  name - required
     *  originalAmount - required
     *  restAmount - required
     *  giftCardFrom - required
     *  message - required
     *  messageUrl
     *  image - required
     *  status - FULL_CREDIT, USED, EMPTY, EXPIRED - required
     *  usageArea - ALL_CHAINS, SITE_WISE, CHAIN_WISE - required
     *  usageDescription - required
     *  expirationDate - required
     */
    item: {
      type: Object,
      default: false,
    },
  },
  computed: {
    giftCardValue() {
      switch (this.item.status) {
        case GiftCardStatus.FULL_CREDIT:
          return "Kr " + this.formatExample(this.item.originalAmount, 2);
        case GiftCardStatus.USED:
        case GiftCardStatus.SITE_UNACTIVE:
          return (
            "Kr " +
            this.formatExample(this.item.restAmount, 1) +
            " (av " +
            this.formatExample(this.item.originalAmount, 2) +
            ")"
          );
        case GiftCardStatus.EMPTY:
        case GiftCardStatus.EXPIRED:
          return (
            "Kr " +
            0 +
            " (av " +
            this.formatExample(this.item.originalAmount, 2) +
            ")"
          );
      }
    },
    giftCardButtonText() {
      switch (this.item.status) {
        case GiftCardStatus.FULL_CREDIT:
        case GiftCardStatus.USED:
          return "Bruk gavekort";
        case GiftCardStatus.EMPTY:
          return "Gavekort oppbrukt";
        case GiftCardStatus.EXPIRED:
        case GiftCardStatus.SITE_UNACTIVE:
          return "Gavekort utløpt";
      }
    },
    flipButtonAvailable() {
      return (
        (this.item.usageArea == GiftCardUsageArea.CHAIN_WISE ||
          this.item.usageArea == GiftCardUsageArea.SITE_WISE) &&
        this.item.messageUrl
      );
    },

    buttonDisabled() {
      return (
        this.item.status == GiftCardStatus.EMPTY ||
        this.item.status == GiftCardStatus.EXPIRED ||
        this.item.status == GiftCardStatus.SITE_UNACTIVE
      );
    },
    shouldDisplayUsageDescription() {
      return this.item.status !== GiftCardStatus.EXPIRED;
    },
    shouldDisplayExpirationDate() {
      return this.item.status !== GiftCardStatus.SITE_UNACTIVE;
    },
  },
  methods: {
    rotateImage() {
      this.flipped = !this.flipped;
    },
    formatText(text) {
      return text.replace(/\r?\n/g, "</br>");
    },
    formatDate(date) {
      return moment(date).format("DD.MM.YYYY.");
    },
    formatExample(value, format) {
      if (value % 1 == 0 && format == 1) {
        return value + ", –";
      } else if (value % 1 == 0 && format == 2) {
        return value;
      } else {
        return value.toFixed(2).replace(".", ",");
      }
    },
    showBarcode() {
      this.$emit("show-barcode", this.item);
    },
  },
};
</script>
<style scoped lang="scss">
.my-gift-card.salon-design {
  border: 2px solid color-mix(in srgb, var(--v-primary-base) 24%, transparent) !important ;
  .chat {
    background-color: color-mix(
      in srgb,
      var(--v-primary-base) 12%,
      transparent
    ) !important ;

    .triangle {
      border-right-color: color-mix(
        in srgb,
        var(--v-primary-base) 12%,
        transparent
      ) !important ;
    }
  }
}
.my-gift-card.fixit-design {
  border: 2px solid #dff2f4 !important;
  color: var(--v-primary-base);

  .chat {
    background-color: #dff2f4 !important;
    .triangle {
      border-right-color: #dff2f4 !important;
    }
    .content {
      font-style: italic;
      line-height: 23.3px;
    }
  }
}
.my-gift-card {
  border-radius: 8px 8px 0px 0px !important;
  display: flex;
  flex-direction: column;
  .custom-img {
    .flip-button {
      position: absolute;
      right: 11px;
      top: 12px;
      z-index: 1;
    }
    border-radius: 8px !important;
    margin-left: 0px;
    margin-right: -2px;
    margin-left: -2px;
    margin-top: -2px;
    max-width: 354px !important;
    height: 222px;
    .video {
      display: flex;
      width: 100% !important;
      height: 222px;
      border: 2px solid #2222225c !important;
      border-radius: 8px !important;
      background: #c0c0c0;
    }
    .v-image {
      height: 222px;
      border: 2px solid #2222225c;
      border-radius: 8px !important;
    }
  }
  .custom-info {
    margin-top: 10px;
    .header {
      margin: 0 10px 0px 10px;
      .custom-title {
        font-size: 18px;
        font-weight: 700;
        line-height: 22px;
        text-align: center;
      }
      .from {
        padding-left: 14px;
        font-size: 18px;
        font-weight: 400;
        line-height: 24px;
        text-align: left;
        margin-top: 8px;
        span {
          font-size: 18px;
          font-weight: 700;
          line-height: 24px;
          text-align: left;
        }
      }
      .chat {
        position: relative;
        margin-left: 12px;
        border-radius: 12px 12px 12px 0px;
        padding: 16px;
        margin-top: 8px;
        margin-bottom: 8px;

        .triangle {
          position: absolute;
          left: 0;
          bottom: 0;
          border: 12px solid transparent;
          border-left: 0;
          border-bottom: 0;
          margin-left: -12px;
        }
        .content {
          font-size: 20px;
          font-weight: 400;
          line-height: 25px;
        }
      }
    }
    .holder {
      padding: 10px 4px 0px 14px;
      margin-bottom: 8px;
      .field {
        padding: 0px 0px 8px 10px !important;
        gap: 150px;
        font-size: 14px;
        font-weight: 400;
        line-height: 16.31px;
      }
    }
    .custom-button {
      margin: 0 10px 24px 10px;
      .fixit-btn {
        text-transform: unset !important;
        &.disabled {
          opacity: 0.5;
          cursor: default;
          pointer-events: none;
        }
      }
    }
  }
}
.flip-enter-active {
  transition: all 1.5s ease;
  .v-btn {
    opacity: 0;
  }
}
.flip-leave-active {
  display: none;
}
.flip-enter,
.flip-leave {
  transform: rotateY(180deg) rotate(5deg);
  opacity: 0;
}
</style>
